import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import axios from 'axios';
import { Award, TrendingUp, Zap, Mail, Loader } from 'lucide-react';

import portalT from '../translations/portal';

const API = `${process.env.REACT_APP_BACKEND_URL || ''}/api`;

const LEVEL_LABELS = {
  en: { beginner: 'Beginner', intermediate: 'Intermediate', advanced: 'Advanced', expert: 'Expert' },
  nl: { beginner: 'Beginner', intermediate: 'Gevorderd', advanced: 'Geavanceerd', expert: 'Expert' },
};

const LEVEL_COLORS = {
  beginner: '#e0e0e0',
  intermediate: '#fbbf24',
  advanced: '#3b82f6',
  expert: '#10b981',
};

const ReadinessScore = () => {
  const { token } = useAuth();
  const { lang } = useLanguage();
  const [data, setData] = useState(null);
  const [weeklyEnabled, setWeeklyEnabled] = useState(false);
  const [toggling, setToggling] = useState(false);

  useEffect(() => {
    const fetch = async () => {
      try {
        const [scoreRes, reportRes] = await Promise.all([
          axios.get(`${API}/readiness/score`, { headers: { Authorization: `Bearer ${token}` } }),
          axios.get(`${API}/readiness/weekly-report-status`, { headers: { Authorization: `Bearer ${token}` } }),
        ]);
        setData(scoreRes.data);
        setWeeklyEnabled(reportRes.data.enabled);
      } catch (e) { console.error('Readiness score error:', e); }
    };
    fetch();
  }, [token]);

  const toggleWeekly = async () => {
    setToggling(true);
    try {
      const res = await axios.post(`${API}/readiness/toggle-weekly-report`, {}, { headers: { Authorization: `Bearer ${token}` } });
      setWeeklyEnabled(res.data.enabled);
    } catch (e) { console.error(e); }
    finally { setToggling(false); }
  };

  if (!data) return null;

  const levelLabel = LEVEL_LABELS[lang]?.[data.level] || data.level;
  const levelColor = LEVEL_COLORS[data.level] || '#e0e0e0';
  const circumference = 2 * Math.PI * 42;
  const offset = circumference - (data.score / 100) * circumference;
  const t = portalT[lang];

  return (
    <div className="readiness-card" data-testid="readiness-score">
      <div className="readiness-header">
        <Award size={20} />
        <h3 className="heading-3">
          {lang === 'nl' ? 'Gebruiksoverzicht' : 'Usage Overview'}
        </h3>
      </div>

      <div className="readiness-body">
        {/* Circular progress */}
        <div className="readiness-ring-wrap">
          <svg width="110" height="110" viewBox="0 0 110 110">
            <circle cx="55" cy="55" r="42" fill="none" stroke="var(--border-light)" strokeWidth="8" />
            <circle
              cx="55" cy="55" r="42" fill="none"
              stroke={levelColor}
              strokeWidth="8"
              strokeLinecap="round"
              strokeDasharray={circumference}
              strokeDashoffset={offset}
              transform="rotate(-90 55 55)"
              style={{ transition: 'stroke-dashoffset 1s ease' }}
            />
          </svg>
          <div className="readiness-ring-text">
            <span className="readiness-pct">{data.score}%</span>
            <span className="readiness-level" style={{ color: levelColor }}>{levelLabel}</span>
          </div>
        </div>

        {/* Stats */}
        <div className="readiness-stats">
          <div className="readiness-stat">
            <Zap size={16} />
            <div>
              <span className="readiness-stat-val">{data.tools_used}/{data.total_tools}</span>
              <span className="readiness-stat-lbl">{lang === 'nl' ? 'Tools gebruikt' : 'Tools used'}</span>
            </div>
          </div>
          <div className="readiness-stat">
            <TrendingUp size={16} />
            <div>
              <span className="readiness-stat-val">{data.total_sessions}</span>
              <span className="readiness-stat-lbl">{lang === 'nl' ? 'Totale sessies' : 'Total sessions'}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Tool bars */}
      <div className="readiness-tools">
        {(Array.isArray(data.tools) ? data.tools : []).map((tool) => (
          <div key={tool.id} className="readiness-tool-row" data-testid={`readiness-tool-${tool.id}`}>
            <span className={`readiness-dot ${tool.used ? 'active' : ''}`} />
            <span className="readiness-tool-name">{t.tools[tool.id]?.title || tool.name}</span>
            <span className="readiness-tool-count">{tool.sessions}</span>
          </div>
        ))}
      </div>
      {/* Weekly report toggle */}
      <div className="readiness-weekly-toggle" data-testid="weekly-report-toggle">
        <Mail size={14} />
        <span className="readiness-tool-name">{lang === 'nl' ? 'Wekelijks rapport per e-mail' : 'Weekly report via email'}</span>
        <button
          onClick={toggleWeekly}
          disabled={toggling}
          className={`toggle-switch ${weeklyEnabled ? 'active' : ''}`}
          data-testid="weekly-toggle-btn"
        >
          <span className="toggle-knob" />
        </button>
      </div>
    </div>
  );
};

export default ReadinessScore;
